// Can 73: a change to who belongs to a World (invite, resend, withdraw, revoke, restore, remove for good), stored
// by the World as a card before anything changes (docs/world-members-design.md 5.3-5.4). The founder approves it
// only on this card (gate HG-WORLD-GOVERN-02, card capability world-members-card-v1): every line is the World's,
// shown as stored, the "When you approve" line included. The Desk adds only the change's key and the expiry.
import {
  decodeMemberChange,
  MEMBER_TOUCH_ID_KINDS,
  type MemberChange,
} from "@cleo/shared/memberChangeCard";
import { decodeJsonObject, postLocalJson } from "./localJsonTransport";

// Can 47: the card's shape, decoding and rows are shared with Pocket (@cleo/shared/memberChangeCard).
export {
  decodeMemberChange,
  memberChangeRows,
  WHEN_YOU_APPROVE,
  type MemberChange,
} from "@cleo/shared/memberChangeCard";

/** A stored change this Desk cannot read as a card (a newer shape, or a damaged answer). */
export const MEMBER_CHANGE_UNREADABLE =
  "Neucleos sent a change this Desk cannot show as a card. Update Cleo on the Desk.";

export type MemberChangeRead =
  | { readonly status: "reading" }
  | { readonly status: "loaded"; readonly change: MemberChange }
  | { readonly status: "failed"; readonly reason: string };

/** The stored change a card shows, read from the World under the founder's own grant. Never throws. */
export async function readMemberChange(
  worldKey: string,
  changeKey: string,
  fetchImpl: typeof fetch = fetch,
): Promise<MemberChangeRead> {
  try {
    const response = await postLocalJson(
      "/api/neucleos/members/change/read",
      { worldKey, changeKey },
      fetchImpl,
      AbortSignal.timeout(30_000),
    );
    const body = await decodeJsonObject(response);
    if (!response.ok || body?.ok !== true)
      throw new Error(
        typeof body?.reason === "string" ? body.reason : "Neucleos could not read this change.",
      );
    let loaded: MemberChange;
    try {
      loaded = decodeMemberChange(body.value);
    } catch {
      return { status: "failed", reason: MEMBER_CHANGE_UNREADABLE };
    }
    rememberCardReceipt(worldKey, changeKey, body.cardReceipt, loaded.kind);
    return { status: "loaded", change: loaded };
  } catch (error) {
    return {
      status: "failed",
      reason: error instanceof Error ? error.message : "Neucleos could not read this change.",
    };
  }
}

// ---- W-dd5db5e2: the Members card's read receipt ---------------------------------------------------------------
// The World approves a change to who belongs to a World only with a one-use receipt its card's own read of that change
// was answered (backend lib/memberCardReceipt.js): 5 minutes at most, bound to this sign-in and to the approvals waiting
// when it was read. This card keeps it here, in this window's memory only, and its Approve sends it back
// (gateInstancesClient decideGateInstance). Nothing else in the Desk reads it.
type HeldReceipt = {
  // null once an Approve spent it: the card keeps its change, so its next Approve reads it again (Codex r1 P2, #2746).
  readonly token: string | null;
  readonly expiresAt: number;
  readonly worldKey: string;
  readonly changeKey: string;
  // The change's kind: an add, a removal or a role change is approved with Touch ID first (memberApprovalNeedsTouchId).
  readonly kind: string;
};
const heldReceipts = new Map<string, HeldReceipt>();
const receiptKey = (worldKey: string, gateInstanceKey: string) => `${worldKey}\n${gateInstanceKey}`;
// A receipt this close to its expiry is read again before Approve rather than sent and refused.
const RECEIPT_MARGIN_MS = 15_000;

function rememberCardReceipt(
  worldKey: string,
  changeKey: string,
  raw: unknown,
  kind: string,
): void {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) return;
  const r = raw as Record<string, unknown>;
  const keys = r["gateInstanceKeys"];
  if (
    typeof r["token"] !== "string" ||
    !/^[0-9a-f]{64}$/.test(r["token"]) ||
    typeof r["expiresAt"] !== "number" ||
    !Array.isArray(keys)
  )
    return;
  for (const key of keys)
    if (typeof key === "string" && key)
      heldReceipts.set(receiptKey(worldKey, key), {
        token: r["token"],
        expiresAt: r["expiresAt"],
        worldKey,
        changeKey,
        kind,
      });
}

/** The kinds that add, remove or change the role of a member (server memberTouchIdApproval.ts MEMBER_PRESENCE_KINDS). */
const TOUCH_ID_KINDS = MEMBER_TOUCH_ID_KINDS;

/** W-786fe139: whether a change of this kind is approved with Touch ID on its Members card (never by a conversation's Approve). */
export function memberChangeKindNeedsTouchId(kind: string): boolean {
  return TOUCH_ID_KINDS.has(kind);
}

/**
 * W-dd5db5e2: whether this card's Approve asks for the person's Touch ID first: an approval of a change this card read
 * that adds, removes or changes the role of a member (the World requires it once its switch is on).
 */
export function memberApprovalNeedsTouchId(worldKey: string, gateInstanceKey: string): boolean {
  const held = heldReceipts.get(receiptKey(worldKey, gateInstanceKey));
  return held !== undefined && TOUCH_ID_KINDS.has(held.kind);
}

/**
 * The receipt this card's read of the change gave for this approval, read again from the World when the one held has
 * expired or been spent (the card is still showing that change); null when the World gave none (an older World, a change
 * no longer waiting, another person's card). Never throws.
 */
export async function memberCardReceiptFor(
  worldKey: string,
  gateInstanceKey: string,
  fetchImpl: typeof fetch = fetch,
  now: () => number = Date.now,
): Promise<string | null> {
  const held = heldReceipts.get(receiptKey(worldKey, gateInstanceKey));
  if (!held) return null;
  if (held.token !== null && held.expiresAt - RECEIPT_MARGIN_MS > now()) return held.token;
  // The card still shows this change: its read again, which is answered a fresh receipt when the change still waits.
  await readMemberChange(worldKey, held.changeKey, fetchImpl);
  const fresh = heldReceipts.get(receiptKey(worldKey, gateInstanceKey));
  return fresh &&
    fresh.token !== null &&
    fresh.token !== held.token &&
    fresh.expiresAt - RECEIPT_MARGIN_MS > now()
    ? fresh.token
    : null;
}

/**
 * A receipt the World accepted is spent: its token is forgotten for every approval it named, while each keeps the change
 * its card shows, so a sibling card's Approve reads that change again for a receipt of its own.
 */
export function spendMemberCardReceipt(token: string): void {
  for (const [key, held] of heldReceipts)
    if (held.token === token) heldReceipts.set(key, { ...held, token: null });
}
